import { useCallback } from 'react'
import { useI18n } from '../../i18n'
import type { CVExperience } from '../../types/cv'
import './editor.css'

export interface ExperienceEditorProps {
  data: CVExperience[]
  onChange: (data: CVExperience[]) => void
}

/**
 * Experience section editor
 */
export function ExperienceEditor({ data, onChange }: ExperienceEditorProps) {
  const { t } = useI18n()
  const handleAdd = useCallback(() => {
    const newEntry: CVExperience = {
      id: crypto.randomUUID(),
      company: '',
      position: '',
      location: '',
      startDate: '',
      endDate: null,
      isCurrent: false,
      description: '',
      sortOrder: data.length,
    }
    onChange([...data, newEntry])
  }, [data, onChange])

  const handleRemove = useCallback(
    (id: string) => {
      onChange(data.filter((item) => item.id !== id))
    },
    [data, onChange]
  )

  const handleChange = useCallback(
    (id: string, field: keyof CVExperience, value: string | boolean | null) => {
      onChange(data.map((item) => (item.id === id ? { ...item, [field]: value } : item)))
    },
    [data, onChange]
  )

  return (
    <>
      <ul className="editor-entry-list" aria-label={t('editor.lists.experience')}>
        {data.map((exp, index) => {
          const entryLabel =
            exp.position || exp.company || t('editor.entries.experience', { number: index + 1 })
          return (
            <li key={exp.id} className="editor-entry" aria-label={entryLabel}>
              <div className="editor-entry-header">
                <span className="editor-entry-title">{entryLabel}</span>
                <div className="editor-entry-actions">
                  <button
                    type="button"
                    className="editor-btn editor-btn-danger editor-btn-icon"
                    onClick={() => handleRemove(exp.id)}
                    aria-label={t('actions.remove', { item: entryLabel })}
                  >
                    ×
                  </button>
                </div>
              </div>

              <div className="editor-grid">
                <div className="editor-field">
                  <label htmlFor={`exp-position-${exp.id}`}>{t('fields.position')}</label>
                  <input
                    id={`exp-position-${exp.id}`}
                    type="text"
                    value={exp.position}
                    onChange={(e) => handleChange(exp.id, 'position', e.target.value)}
                    placeholder={t('placeholders.position')}
                  />
                </div>

                <div className="editor-field">
                  <label htmlFor={`exp-company-${exp.id}`}>{t('fields.company')}</label>
                  <input
                    id={`exp-company-${exp.id}`}
                    type="text"
                    value={exp.company}
                    onChange={(e) => handleChange(exp.id, 'company', e.target.value)}
                    placeholder={t('placeholders.company')}
                  />
                </div>

                <div className="editor-field">
                  <label htmlFor={`exp-location-${exp.id}`}>{t('fields.location')}</label>
                  <input
                    id={`exp-location-${exp.id}`}
                    type="text"
                    value={exp.location}
                    onChange={(e) => handleChange(exp.id, 'location', e.target.value)}
                    placeholder={t('placeholders.location')}
                  />
                </div>

                <div className="editor-date-range">
                  <div className="editor-field">
                    <label htmlFor={`exp-startDate-${exp.id}`}>{t('fields.startDate')}</label>
                    <input
                      id={`exp-startDate-${exp.id}`}
                      type="month"
                      value={exp.startDate}
                      onChange={(e) => handleChange(exp.id, 'startDate', e.target.value)}
                    />
                  </div>

                  <div className="editor-field">
                    <label htmlFor={`exp-endDate-${exp.id}`}>{t('fields.endDate')}</label>
                    <input
                      id={`exp-endDate-${exp.id}`}
                      type="month"
                      value={exp.endDate || ''}
                      onChange={(e) => handleChange(exp.id, 'endDate', e.target.value || null)}
                      disabled={exp.isCurrent}
                      aria-disabled={exp.isCurrent}
                    />
                  </div>
                </div>

                <div className="editor-field editor-field-full">
                  <label className="editor-checkbox">
                    <input
                      type="checkbox"
                      checked={exp.isCurrent}
                      onChange={(e) => {
                        handleChange(exp.id, 'isCurrent', e.target.checked)
                        if (e.target.checked) {
                          handleChange(exp.id, 'endDate', null)
                        }
                      }}
                    />
                    {t('fields.currentWork')}
                  </label>
                </div>

                <div className="editor-field editor-field-full">
                  <label htmlFor={`exp-description-${exp.id}`}>{t('fields.description')}</label>
                  <textarea
                    id={`exp-description-${exp.id}`}
                    className="editor-textarea-long"
                    value={exp.description}
                    onChange={(e) => handleChange(exp.id, 'description', e.target.value)}
                    placeholder={t('placeholders.experienceDescription')}
                    rows={8}
                  />
                </div>
              </div>
            </li>
          )
        })}
      </ul>

      <button type="button" className="editor-add-btn" onClick={handleAdd}>
        <span aria-hidden="true">+</span> {t('actions.addExperience')}
      </button>
    </>
  )
}
